"use client";

import { useRef, useState, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { GlassComposer } from "@/components/chat/GlassComposer";
import { useInFlightGuard } from "@/hooks/use-in-flight-guard";
import { useSupportImageDraft } from "@/hooks/useSupportImageDraft";
import {
  sendSupportMessage,
  startSupportConversation,
} from "@/app/actions/support";
import type { LangCode } from "@/types/message";
import type { SupportConversation, SupportMessage } from "@/types/support";
import { SupportImageButton, SupportImageTray } from "./SupportImageAttach";

interface SupportComposerProps {
  conversation: SupportConversation | null;
  /** 키보드가 홈 인디케이터를 덮는 동안 safe-area 패딩을 떨어뜨린다 —
   *  유지하면 입력창이 키보드 위에 떠서 여백이 보인다. */
  keyboardOpen?: boolean;
  onConversationCreated: (conv: SupportConversation) => void;
  onMessageSent: (msg: SupportMessage) => void;
}

const MAX_LEN = 4000;

export function SupportComposer({
  conversation,
  keyboardOpen = false,
  onConversationCreated,
  onMessageSent,
}: SupportComposerProps) {
  const t = useTranslations("support.composer");
  const tErrors = useTranslations("support.errors");
  const locale = useLocale() as LangCode;

  const [text, setText] = useState("");
  const [isPending, startTransition] = useTransition();
  // 연속 입력에 대화가 둘 생기지 않게 하고, 재시도는 같은 id 로 서버에서 합친다.
  const { run: runSendGuarded } = useInFlightGuard();
  const sendIdRef = useRef<string | null>(null);
  const imageDraft = useSupportImageDraft();

  const handleSubmit = () => {
    const value = text.trim();
    if ((!value && imageDraft.items.length === 0) || isPending) return;
    if (value.length > MAX_LEN) {
      toast.error(t("tooLong"));
      return;
    }

    startTransition(async () => {
      await runSendGuarded(async () => {
      // Lazily create the conversation on first message.
      let activeConv = conversation;
      if (!activeConv) {
        const res = await startSupportConversation({
          locale,
          lastSeenUrl:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        });
        if (!res.data) {
          toast.error(tErrors(res.error ?? "UNEXPECTED_ERROR"));
          return;
        }
        activeConv = res.data;
        onConversationCreated(activeConv);
      }

      const attachments = await imageDraft.uploadAll(activeConv.id);
      if (!attachments) return;

      const messageId = sendIdRef.current ?? crypto.randomUUID();
      sendIdRef.current = messageId;
      const sendRes = await sendSupportMessage({
        id: messageId,
        conversation_id: activeConv.id,
        content: value,
        attachments,
        original_lang_code: locale,
        // Always auto-translate to KO when the customer is on a non-KO locale
        // so super_admin sees the message in Korean without extra friction.
        translate: locale !== "ko",
      });
      if (!sendRes.data) {
        // Keep sendIdRef: a retry of the same draft dedupes server-side.
        toast.error(tErrors(sendRes.error ?? "UNEXPECTED_ERROR"));
        return;
      }
      sendIdRef.current = null;
      onMessageSent(sendRes.data);
      setText("");
      imageDraft.clear();
      });
    });
  };

  return (
    <div
      // Aligns horizontally with the message list (px-3) and follows the same
      // safe-area pattern as the 1:1 chat composer in chat-room-page-client.
      className="bg-gray-50 px-3 pt-2"
      style={{
        paddingBottom: keyboardOpen
          ? "0.5rem"
          : "calc(0.5rem + var(--loc-safe-area-inset-bottom))",
      }}
    >
      <SupportImageTray
        items={imageDraft.items}
        onRemove={(id) => {
          sendIdRef.current = null; // attachment set changed → new message
          imageDraft.remove(id);
        }}
      />
      <GlassComposer
        variant="docked"
        value={text}
        onChange={(value) => {
          sendIdRef.current = null; // content changed → new message
          setText(value);
        }}
        onSend={handleSubmit}
        canSend={(!!text.trim() || imageDraft.items.length > 0) && !isPending}
        sending={isPending}
        placeholder={t("placeholder")}
        sendLabel={t("send")}
        invalid={text.length > MAX_LEN}
        textareaProps={{
          maxLength: MAX_LEN + 100,
          // 위젯은 지금처럼 Enter 로 보낸다(Shift+Enter 줄바꿈).
          onKeyDown: (e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing &&
              e.keyCode !== 229
            ) {
              e.preventDefault();
              handleSubmit();
            }
          },
        }}
        leading={
          <SupportImageButton
            count={imageDraft.items.length}
            onAdd={(files) => {
              sendIdRef.current = null; // attachment set changed → new message
              imageDraft.add(files);
            }}
          />
        }
      />
    </div>
  );
}
